import * as _ from 'lodash';
import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CsvHelper, FileHelper, ParserHelper } from 'src/app/helpers';
import { User } from 'src/app/models';
import { CsvBool, CsvSelectOne, CsvUserData } from './upload-users-dialog.viewmodel';

type ValidationError = string;
type ValidationErrors = ValidationError[];

type CsvUserFields = User & {
  completionDate: Date;
};

type Mapping<T> = { col: T, map: (value: string) => any, required?: boolean, pattern?: RegExp };
type MappingColumn = Mapping<keyof CsvUserData>;

type Parsed<T> = KeysAs<T, any>;
type ParsedUserFields = Parsed<CsvUserFields>;

const v = ParserHelper;
const toCsvBool = (val: string) => {
  switch (v.toEnumValue(CsvBool, val)) {
    case CsvBool.Yes: return true;
    case CsvBool.No: return false;
    default: return undefined;
  }
};

@Component({
  selector: 'app-upload-users-dialog',
  templateUrl: './upload-users-dialog.component.html',
  styleUrls: ['./upload-users-dialog.component.css']
})
export class UploadUsersDialogComponent implements OnInit {
  readonly TEMPLATE = 'users_to_upload_template.xls';

  downloadedFile = false;
  selectedFile: File|null = null;

  summary = {
    rows:       0,
    parsed:     0,
    invalid:    0,
    errors:     0,
    duplicates: 0,
  };

  parsed: ParsedUserFields[] = [];
  duplicates: string[] = []; // emails

  error: string = '';
  errors: string[] = [];

  // see Excel template: users_to_upload_template.xls
  readonly userMapping: KeysAs<CsvUserFields, MappingColumn|null> = {
    _id             : null,
    firstName       : { col: 'First Name *', map      : v.toString, required: true },
    middleInitial   : { col: 'Middle Initial', map    : v.toString },
    lastName        : { col: 'Last Name *', map       : v.toString, required: true },
    email           : { col: 'Email *', map           : v.toString, required: true },
    phone           : { col: 'Phone *', map           : v.toString, required: true, pattern: /^\+?[( )\d]+$/g },
    completionDate  : { col: 'Completion Date *', map : v.toDate,   required: true },
    ageUnder18      : { col: 'Age Under 18 *', map    : toCsvBool,  required: true },
    dob             : { col: 'Date of Birth', map     : v.toDate,   required: true },
    race            : { col: 'Race', map              : v.toString, required: true },
    ethnicity       : { col: 'Ethnicity', map         : v.toString, required: true },
    disabled        : { col: 'Are They Disabled?', map: toCsvBool,  required: true },
    numberOfChildren: { col: 'Number of Children', map: v.toString, required: true },
    maritalStatus   : { col: 'Marital Status', map    : v.toString, required: true },
    familyStatus    : { col: 'Family Status', map     : v.toString, required: true },
    education       : { col: 'Education', map         : v.toString, required: true },
    annualIncome    : { col: 'Annual Income', map     : v.toString, required: true },
    sourceOfIncome  : { col: 'Source of Income', map  : v.toString, required: true },
    streetAddress   : { col: 'Street Address 1', map  : v.toString, required: true },
    secondary       : { col: 'Street Address 2', map  : v.toString },
    city            : { col: 'City *', map            : v.toString, required: true },
    state           : { col: 'State *', map           : v.toString, required: true },
    zipCode         : { col: 'Zip Code', map          : v.toString, required: true },
    county          : { col: 'County *', map          : v.toString, required: true },
    primaryResidence: { col: 'Primary Residence', map : toCsvBool,  required: true },
    livingSituation : { col: 'Living Situation', map  : v.toString, required: true },
  };

  readonly userMappingFields = Object.keys(this.userMapping) as Array<keyof CsvUserFields>;
  readonly userMappingCols = Object.values(this.userMapping).map(m => m?.col as string).filter(col => col);

  // map less fields for users under age 18
  readonly userMappingAgeUnder18: KeysAs<Partial<CsvUserFields>, MappingColumn|null> = {
    firstName     : { col: 'First Name *', map     : v.toString, required: true },
    middleInitial : { col: 'Middle Initial', map   : v.toString },
    lastName      : { col: 'Last Name *', map      : v.toString, required: true },
    email         : { col: 'Email *', map          : v.toString, required: true },
    completionDate: { col: 'Completion Date *', map: v.toDate,   required: true },
    ageUnder18    : { col: 'Age Under 18 *', map   : toCsvBool,  required: true },
    city          : { col: 'City *', map           : v.toString, required: true },
    state         : { col: 'State *', map          : v.toString, required: true },
    county        : { col: 'County *', map         : v.toString, required: true },
  };

  constructor(
    private dialogRef: MatDialogRef<UploadUsersDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: any,
    private cd: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    console.log('init upload');
  }

  get enableUpload(): boolean {
    return Boolean(
      this.selectedFile &&
      this.summary.parsed > 0 &&
      this.summary.invalid === 0 &&
      this.summary.errors === 0
    );
  }

  async downloadTemplate() {
    const xlsBytes = await fetch("../../../../../assets/" + this.TEMPLATE).then(res => res.arrayBuffer());
    FileHelper.saveByteArray(this.TEMPLATE, xlsBytes, 'application/vnd.ms-excel');
    this.downloadedFile = true;
  }

  async loadUsersFrom(target: EventTarget|null) {
    const input = target as HTMLInputElement;
    const file = input?.files ? input.files.item(0) : null;

    this.selectedFile = file;

    // 0. Reset
    this.resetData();
    this.resetSelectedFile(input);
    this.resetSummary();

    if (!file) {
      return;
    }

    // 1. Parse CSV
    const data = await this.parseCsv(file);

    if (!data) {
      this.summary.errors++;
      console.log('failed to parse csv file', file.name);
      return;
    }

    this.summary.rows = data.length;

    // 2. Validate schema
    const errors = this.validateSchema(data);

    if (errors.length) {
      this.errors = _.uniq(errors);
      this.summary.errors = this.errors.length;
      return;
    }

    // 3. Map row to ParsedUser, skip empty rows
    const parsed = data
      .filter(row => this.isEmptyRow(row) === false) // skip empty rows
      .map(row => this.mapUserFrom(row)) // row -> user
      .filter(data => this.isEmptyUser(data) === false); // skip empty users (no data entered, only "-- select one --" placeholders left)

    this.parsed = parsed;
    this.summary.parsed = parsed.length;

    // 4. Validate each ParsedUser
    parsed.forEach(user => {
      const errors = this.validateEach(user);

      if (errors.length) {
        this.summary.invalid++; // row
        this.errors.push(...errors);
      }
    });

    // 5. Validate duplicates in CSV by email address
    parsed.forEach((user, index) => {
      const next = parsed.slice(index + 1);
      const errors = this.validateDuplicates(next, user);

      if (errors.length) {
        this.duplicates.push(user.email);
      }
    });

    // 6. Collect duplicates
    if (this.duplicates.length) {
      this.duplicates = _.uniq(this.duplicates);
      this.summary.duplicates = this.duplicates.length;
    }

    // 7. Collect errors
    if (this.errors.length) {
      this.errors = _.uniq(this.errors);
      this.summary.errors = this.errors.length;
    }
  }

  upload() {
    if (!this.enableUpload) {
      return;
    }

    this.dialogRef.close({ users: this.parsed });
  }

  close() {
    this.dialogRef.close();
  }

  private async parseCsv(file: File) {
    const data = await CsvHelper.parseFromFile<CsvUserData>(file).catch(error => {
      if (error.length) {
        this.error = error[0].message || 'csv format error';
      }
      else if (error.message) {
        this.error = error.message;
      }
      else {
        this.error = 'csv format error';
      }
    });

    this.cd.markForCheck();

    return data;
  }

  private validateSchema(data: CsvUserData[]): ValidationErrors {
    const errors = [] as ValidationErrors;

    if (!data.length) {
      errors.push('No users found in CSV file.');
    }

    if (data.length) {
      this.errors.push(...this.validateColumns(data));
    }

    return errors;
  }

  private validateColumns(data: CsvUserData[]): ValidationErrors {
      const cols = Object.keys(data[0]);
      const expected = this.userMappingCols;
      const errors = expected.filter(e => !cols.includes(e)).map(col => `${col} column is missing.`);
      return errors;
  }

  private validateEach(user: ParsedUserFields): ValidationErrors {
    const errors = [] as ValidationErrors;

    for (const field of this.userMappingFields) {
      const mapping = user.ageUnder18 ? this.userMappingAgeUnder18[field] : this.userMapping[field];
      const value = user[field];

      if (value && value.$error) {
        errors.push(value.$error);
        continue;
      }

      if (!mapping) {
        continue;
      }

      if (mapping.required && (value === CsvSelectOne || this.isEmpty(value))) {
        let col = String(mapping.col);

        // trim " *" at the end for mandatory fields
        if (col.endsWith(' *')) {
          col = col.substring(0, col.length - 2);
        }

        errors.push(`${col} is required.`);
      }
    }

    return errors;
  }

  private validateDuplicates(next: ParsedUserFields[], user: ParsedUserFields): ValidationErrors {
    if (!next.length) {
      return [];
    }

    const errors: ValidationErrors = [];

    if (user.email && next.some(u => u.email.toLowerCase() === user.email.toLowerCase())) {
      errors.push(`Found duplicates for email: ${user.email}`);
    }

    return errors;
  }

  private mapUserFrom(row: CsvUserData): ParsedUserFields {
    const user = {} as any;

    for (const field of this.userMappingFields) {
      const mapping: MappingColumn = (this.userMapping as any)[field];

      if (mapping) {
        const raw = row[mapping.col];
        const map = mapping.map;
        const val = map(raw);

        let value = map === v.toString ? val.trim() : val;

        if (mapping.pattern) {
          value = (String(value).match(mapping.pattern) || []).join('');
          mapping.pattern.lastIndex = 0; // reset regex for next use
        }

        user[field] = value !== CsvSelectOne ? value : null;

        const invalid = raw && raw !== CsvSelectOne && this.isEmpty(value);

        if (invalid) {
          if (map === v.toDate) {
            user[field] = { $error: `Invalid date format for ${mapping.col}: "${raw}"` };
          }
          else {
            user[field] = { $error: `Invalid value for ${mapping.col}: "${raw}"` };
          }
        }
      }
    }

    return user;
  }

  private isEmpty(value: unknown): boolean {
    return value === null || value === undefined || (typeof value === 'string' ? value.trim() === '' : false);
  }

  private isEmptyRow(data: CsvUserData): boolean {
    return Object.keys(data).every(col => this.isEmpty(data[col]));
  }

  private isEmptyUser(user: ParsedUserFields): boolean {
    for (const field of this.userMappingFields) {
      const mapping = user.ageUnder18 ? this.userMappingAgeUnder18[field] : this.userMapping[field];
      const value = user[field];

      if (!mapping) {
        continue;
      }

      if (value && value !== CsvSelectOne && !this.isEmpty(value)) {
        return false;
      }
      else {
        continue;
      }
    }

    return true;
  }

  private resetData() {
    this.error = '';
    this.errors = [];
    this.parsed = [];
    this.duplicates = [];
  }

  private resetSelectedFile(input: HTMLInputElement) {
    input.value = '';
  }

  private resetSummary() {
    this.summary = {
      rows:       0,
      parsed:     0,
      invalid:    0,
      errors:     0,
      duplicates: 0,
    };
  }
}
